import type { Unary } from '@vaaas/fpts/data';
import { entries } from '@vaaas/fpts/object';
import type { Newtype } from '@phi/newtype';
import { add } from '@vaaas/fpts/maths';
import { mapMonth, mapYear } from '@vaaas/fpts/date';
import { sleep } from '@vaaas/fpts/promise';
import { PromiseResult } from '@vaaas/fpts/promise-result';

export function attempt<T>(f: () => Promise<T>): Promise<T | Error>;
export function attempt<T>(f: () => T): T | Error;
export function attempt<T>(f: () => T | Promise<T>): T | Error | Promise<T | Error> {
    try {
        const x = f();
        if (x instanceof Promise)
            return x.catch(e => e);
        else
            return x;
    } catch(e) {
        if (e instanceof Error)
            return e;
        else
            return new Error(''+e);
    }
}

export function read_file_as_text(file: File): Promise<string | ArrayBuffer | Error> {
    return new Promise((yes) => {
        const reader = new FileReader();
        reader.onload = () => yes(
            typeof reader.result !== 'string'
                ? new Error('null file contents')
                : reader.result
        );
        reader.onerror = () => yes(new Error('error loading file'));
        reader.readAsText(file);
    });
}

export const tap = <T>(f: Unary<T, void>) => (x: T): T => { f(x); return x; };

export const print = tap(console.log) as <T>(x: T) => T;

const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
export function random_id(n=4) {
    let s = '';
    for (let i = 0; i < 4; i++)
        s += chars[Math.floor(Math.random() * chars.length)];
    return s;
}

/** remove hotfix versions from the version string
 *
 * 1.0.0-124 -> 1.0.0
 * 1.0.0 -> 1.0.0
 */
export const nice_version_format = (x: string) => {
    const i = x.lastIndexOf('-');
    if (i === -1) return x;
    else return x.slice(0, i);
};

export const is_empty_array = (x: unknown): x is never[] => Array.isArray(x) && x.length === 0;

export const make_url = (base: string, query: undefined | Record<string, string | Newtype<string>> = undefined) => {
    if (query)
        return base + '?' + entries(query).map(([k, v]) => `${k}=${v}`).join('&');
    else
        return base;
};

export const seconds = (x: number) => x * 1_000;
export const minutes = (x: number) => x * seconds(60);
export const hours = (x: number) => x * minutes(60);
export const days = (x: number) => x * hours(24);
export const months = (x: number) => x * days(30);

export const always_true = () => true;
export const do_nothing = () => undefined;

export function pretty_print_record(x: Record<string, string|number>): string {
    return Object.entries(x).map(x => `${x[0]}: ${x[1]}`).join(', ');
}

const inc = add(1);
const dinc = add(-1);

const add_one_month = mapMonth(inc);
export const next_month = () => add_one_month(new Date());

const sub_one_month = mapMonth(dinc);
export const previous_month = () => sub_one_month(new Date());

const add_one_year = mapYear(inc);
export const next_year = () => add_one_year(new Date());

export const minify_html = (x: string) => x.trim().replaceAll(/\s+/g, ' ');

/** kestrel combinator wrapped in a promise
 *
 * @param x argument to wrap in a promise
 * @returns a function that, when called, resolves to `x` wrapped in a promise
 */
export const KP = <X>(x: X) => () => Promise.resolve(x);

/** kestrel combinator wrapped in a promise with a timer
 *
 * @param x argument to wrap in a promise
 * @param ms milliseconds to wait before resolving the promise
 *
 * @returns a function that, when called, resolves to `x` wrapped in a promise after `ms` milliseconds have passed
 */
export const KP_ = <X>(x: X, ms: number) => (): Promise<X> => new Promise(f => setTimeout(() => f(x), ms));

export async function retry_exponential_backoff<T>(f: () => Promise<T>, initial = 500, multiplier = 2, max_retries = Infinity): PromiseResult<T> {
    let i = 0;
    let t = initial;
    while (i <= max_retries) {
        const x = await f().catch(e => e instanceof Error ? e : new Error('something went wrong: ' + e));
        if (x instanceof Error) {
            console.error(x);
            await sleep(t);
            t *= multiplier;
            i++;
        } else
            return x;
    }
    return new Error('max retries exceeded');
}
